import { formatDate } from "./dateUtils";

/**
 * Exports an array of items to a CSV file download.
 * @param {Object[]} data - Array of data objects
 * @param {string} filename - Download filename (e.g. "bottle-feeds.csv")
 * @param {Array<{label: string, key: string, formatter?: Function}>} columns - Column definitions
 */
export const exportToCSV = (data, filename, columns) => {
  if (!data || data.length === 0) {
    alert("No data to export.");
    return;
  }

  const headers = columns.map((c) => `"${c.label}"`).join(",");

  const rows = data.map((item) =>
    columns
      .map((c) => {
        const raw = item[c.key] ?? "";
        const val = c.formatter ? c.formatter(raw) : raw;
        return `"${String(val).replace(/"/g, '""')}"`;
      })
      .join(",")
  );

  const csvContent = [headers, ...rows].join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Pre-built column configs for each section
export const bottleCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Amount (oz)", key: "amount" },
  { label: "Type", key: "type" },
];

export const waterCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Amount (oz)", key: "amount" },
  { label: "Notes", key: "notes" },
];

export const solidFoodCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Food Name", key: "foodName" },
  { label: "Quantity", key: "quantity" },
  { label: "Reactions", key: "reactions" },
];

export const foodIntroCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Food Item", key: "foodItem" },
  { label: "Reaction/Notes", key: "reaction" },
];

export const sleepCSVColumns = [
  { label: "Date", key: "date", formatter: formatDate },
  { label: "Start Time", key: "startTime" },
  { label: "End Time", key: "endTime" },
  { label: "Type", key: "type" },
  { label: "Notes", key: "notes" },
];

export const growthHealthCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Type", key: "type" },
  { label: "Weight (lbs)", key: "weight" },
  { label: "Height (in)", key: "height" },
  { label: "Head Circumference (in)", key: "headCircumference" },
  { label: "Temperature (°F)", key: "temperature" },
  { label: "Medication", key: "medication" },
  { label: "Dosage", key: "dosage" },
  { label: "Symptoms", key: "symptoms" },
  { label: "Severity", key: "severity" },
  { label: "Notes", key: "notes" },
];

export const milestoneCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Category", key: "category" },
  { label: "Milestone", key: "milestone" },
  { label: "Notes", key: "notes" },
];

export const healthRecordCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Record Type", key: "recordType" },
  { label: "Title", key: "title" },
  { label: "Notes", key: "notes" },
];

export const diaperCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Type", key: "type" },
  { label: "Color", key: "color" },
  { label: "Consistency", key: "consistency" },
  { label: "Notes", key: "notes" },
];

export const vaccinationCSVColumns = [
  { label: "Date Given", key: "date", formatter: formatDate },
  { label: "Vaccine Name", key: "vaccineName" },
  { label: "Dose", key: "dose" },
  { label: "Next Due Date", key: "nextDueDate" },
  { label: "Notes", key: "notes" },
];

export const breastfeedingCSVColumns = [
  { label: "Date & Time", key: "date", formatter: formatDate },
  { label: "Side", key: "side" },
  { label: "Duration (min)", key: "duration" },
  { label: "Notes", key: "notes" },
];

export const allergyCSVColumns = [
  { label: "Date", key: "date", formatter: formatDate },
  { label: "Food Item", key: "foodItem" },
  { label: "Reaction", key: "reaction" },
  { label: "Severity", key: "severity" },
  { label: "Notes", key: "notes" },
];

export const doctorVisitCSVColumns = [
  { label: "Date & Time",              key: "date",             formatter: formatDate },
  { label: "Appointment Type",         key: "appointmentType" },
  { label: "Practitioner",             key: "practitioner" },
  { label: "Reason for Visit",         key: "reason" },
  { label: "Weight (kg)",              key: "weight" },
  { label: "Height (cm)",              key: "height" },
  { label: "Head Circumference (cm)",  key: "headCircumference" },
  { label: "Outcome / Notes",          key: "notes" },
  { label: "Prescriptions",            key: "prescriptions" },
  { label: "Follow-up Date",           key: "followUpDate" },
];
